import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtError } from "@/lib/api";
import { L, fmtDate } from "@/constants/labels";
import { CheckCircle2, AlertTriangle, XCircle, MinusCircle, Upload, Info, Lock, Unlock, Eye, Clock, AlertOctagon, ChevronDown, ChevronRight } from "lucide-react";

const TYPES = ["kbis", "urssaf", "rc_pro", "decennale", "attestation_fiscale"];
const RESTRICTABLE_TYPES = new Set(["urssaf", "attestation_fiscale"]);

// Retourne le statut effectif affichable côté artisan (utilisateur).
// Priorité : refuse > expire > expire_bientot > (statut stocké) > non_fourni.
// Utilise les statuts canoniques 2026-09 (verifie/a_controler/expire_bientot/...).
function statusOf(doc) {
  if (!doc) return { key: "non_fourni", cls: "badge-muted", Icon: MinusCircle };
  const raw = doc.statut;
  if (raw === "refuse") return { key: "refuse", cls: "badge-danger", Icon: XCircle };
  const days = doc.date_expiration ? Math.floor((new Date(doc.date_expiration) - new Date()) / 86400000) : null;
  if (days != null && days < 0) return { key: "expire", cls: "badge-danger", Icon: XCircle };
  // « En cours de vérification » — libellé artisan (a_controler canonique
  // + legacy a_verifier). Clock plutôt que AlertTriangle : c'est une attente,
  // pas une erreur.
  if (raw === "a_controler" || raw === "a_verifier") return { key: "a_controler", cls: "badge-warn", Icon: Clock };
  if (raw === "expire_bientot" || raw === "expirant") return { key: "expire_bientot", cls: "badge-warn", Icon: AlertTriangle };
  if (days != null && days < 30) return { key: "expire_bientot", cls: "badge-warn", Icon: AlertTriangle };
  // verifie (canonique) ou valide (legacy) → à jour
  return { key: "verifie", cls: "badge-valid", Icon: CheckCircle2 };
}

export default function MesDocuments() {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [accessLog, setAccessLog] = useState([]);
  const [slaLabel, setSlaLabel] = useState(L.compliance.slaFallback);

  const load = async () => {
    const { data } = await api.get("/documents/me");
    setDocs(data);
  };
  const loadAccessLog = async () => {
    try {
      const { data } = await api.get("/documents/access-log/me");
      setAccessLog(data);
    } catch (err) {
      // Silencieux — pas de panique si l'endpoint n'est pas déployé
    }
  };
  const loadConfig = async () => {
    try {
      const { data } = await api.get("/documents/config");
      if (data?.sla_label) setSlaLabel(data.sla_label);
    } catch { /* fallback L.compliance.slaFallback */ }
  };
  useEffect(() => { load(); loadAccessLog(); loadConfig(); }, []);

  const upload = async (type, file, date_depot, date_expiration) => {
    setBusy(true);
    const fd = new FormData();
    fd.append("type", type); fd.append("file", file);
    fd.append("date_depot", date_depot); fd.append("date_expiration", date_expiration);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Document déposé — vérification ${slaLabel}`);
      load();
    } catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); }
  };

  const toggleRestriction = async (doc) => {
    try {
      const { data } = await api.patch(`/documents/${doc.id}/restrict`, { restrict: !doc.restrict_to_engagement });
      toast.success(data.restrict_to_engagement
        ? "Ce document est maintenant restreint aux entreprises engagées dans une mission."
        : "Ce document redevient consultable par tout donneur d'ordre vérifié.");
      load();
    } catch (err) { toast.error(fmtError(err)); }
  };

  const byType = Object.fromEntries(docs.map(d => [d.type, d]));

  return (
    <div className="max-w-4xl">
      <h1 className="text-2xl md:text-3xl font-semibold text-[#1F2933]">Mes documents administratifs</h1>
      <p className="text-sm text-[#3E4C59] mt-1">Les documents doivent dater de moins de 3 mois. Formats acceptés : PDF, JPG, PNG (max 10 Mo).</p>

      {/* Bandeau SLA — engagement du délai de vérification, Chantier 2 */}
      <div className="mt-4 rounded-md border border-[#E4EEF7] bg-[#F0F7FE] p-3 text-sm text-[#1F2933] flex items-start gap-2" data-testid="depot-sla-banner">
        <Clock size={16} className="mt-0.5 text-[#1B3A5C] shrink-0" />
        <div>
          <strong>Chaque document est vérifié {slaLabel}.</strong>{" "}
          Vous verrez le statut passer à « À jour » dès qu'il est validé. Si un document ne correspond pas
          (illisible, expiré, mauvais SIRET…), vous recevrez un e-mail avec le motif pour redéposer une version
          exploitable.
        </div>
      </div>

      {/* Mention explicite au dépôt — promesse de la page d'accueil */}
      <div className="mt-3 rounded-md border border-[#DBEAFE] bg-[#EFF6FF] p-3 text-sm text-[#1F2933] flex items-start gap-2" data-testid="depot-info-banner">
        <Info size={16} className="mt-0.5 text-[#1B3A5C] shrink-0" />
        <div>
          <strong>Ce document sera consultable par les donneurs d'ordre vérifiés.</strong>{" "}
          Vous verrez chaque consultation dans le journal en bas de page (raison sociale, SIRET, date).
          Le PDF servi porte un filigrane à l'en-tête du consultant.
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {TYPES.map(t => {
          const d = byType[t]; const s = statusOf(d);
          return (
            <DocRow
              key={t}
              type={t}
              doc={d}
              status={s}
              onUpload={upload}
              onToggleRestrict={toggleRestriction}
              disabled={busy}
            />
          );
        })}
      </div>

      <AccessLogSection log={accessLog} />
    </div>
  );
}

function DocRow({ type, doc, status, onUpload, onToggleRestrict, disabled }) {
  const [file, setFile] = useState(null);
  const [dd, setDd] = useState(new Date().toISOString().slice(0, 10));
  const [de, setDe] = useState("");
  // Chantier 2 (2026-09) — motif de refus toujours visible sur la ligne du
  // document. Un artisan ne retrouvera pas l'e-mail 3 jours plus tard :
  // badge rouge cliquable qui ouvre le détail complet + date de refus +
  // rappel de l'action attendue (redéposer un document exploitable).
  const [motifOpen, setMotifOpen] = useState(false);
  const isRefuse = doc?.statut === "refuse" && doc?.motif;
  const submit = (e) => {
    e.preventDefault();
    if (!file || !de) return;
    onUpload(type, file, dd, de);
    setFile(null);
  };
  const canRestrict = doc && RESTRICTABLE_TYPES.has(type);
  const restricted = !!doc?.restrict_to_engagement;
  return (
    <div className="card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-base font-semibold text-[#1F2933]">{L.docTypes[type]}</div>
          {doc && <div className="text-xs text-[#3E4C59]">Dépôt {fmtDate(doc.date_depot)} • Expire {fmtDate(doc.date_expiration)}</div>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={status.cls} data-testid={`doc-status-${type}`}><status.Icon size={12} /> {L.compliance[status.key]}</span>
          {isRefuse && (
            <button
              type="button"
              onClick={() => setMotifOpen(o => !o)}
              className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold bg-[#FEE2E2] text-[#B91C1C] border border-[#FCA5A5] hover:bg-[#FECACA]"
              data-testid={`doc-motif-badge-${type}`}
              aria-expanded={motifOpen}
            >
              <AlertOctagon size={12} />
              Motif du refus
              {motifOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            </button>
          )}
        </div>
      </div>

      {isRefuse && motifOpen && (
        <div className="mt-3 rounded-md border border-[#FCA5A5] bg-[#FEF2F2] p-3 text-sm text-[#7F1D1D]" data-testid={`doc-motif-detail-${type}`}>
          <div className="font-semibold flex items-center gap-2">
            <AlertOctagon size={14} /> Ce document a été refusé
            {doc.validated_at && (
              <span className="text-xs font-normal text-[#B91C1C]">le {fmtDate(doc.validated_at)}</span>
            )}
          </div>
          <p className="mt-2 whitespace-pre-line">{doc.motif}</p>
          <p className="mt-2 text-xs text-[#B91C1C]">
            Redéposez ci-dessous une version qui répond au motif ci-dessus. Le nouveau
            document sera vérifié sous quelques jours ouvrés.
          </p>
        </div>
      )}

      {canRestrict && (
        <div className="mt-3 flex items-center gap-2 text-sm">
          <button
            type="button"
            onClick={() => onToggleRestrict(doc)}
            className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium border ${
              restricted
                ? "bg-[#FEF3C7] border-[#FCD34D] text-[#92400E]"
                : "bg-white border-[#E4E7EB] text-[#3E4C59] hover:bg-[#F5F6F8]"
            }`}
            data-testid={`doc-restrict-toggle-${type}`}
          >
            {restricted ? <Lock size={12} /> : <Unlock size={12} />}
            {restricted ? "Restreint à l'engagement" : "Consultable par tout donneur d'ordre vérifié"}
          </button>
          <span className="text-xs text-[#3E4C59]">
            {restricted
              ? "Seules les entreprises avec une mission acceptée peuvent l'ouvrir."
              : "Cliquez pour restreindre ce document aux entreprises engagées."}
          </span>
        </div>
      )}

      <form onSubmit={submit} className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
        <div>
          <label className="label">Date de dépôt</label>
          <input type="date" className="input" value={dd} onChange={(e) => setDd(e.target.value)} required data-testid={`doc-depot-${type}`} />
        </div>
        <div>
          <label className="label">Date d'expiration</label>
          <input type="date" className="input" value={de} onChange={(e) => setDe(e.target.value)} required data-testid={`doc-exp-${type}`} />
        </div>
        <div>
          <label className="label">Fichier</label>
          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files?.[0] || null)} className="input" data-testid={`doc-file-${type}`} />
        </div>
        <button disabled={disabled || !file} className="btn-primary" data-testid={`doc-upload-${type}`}><Upload size={16} /> Déposer</button>
      </form>
    </div>
  );
}

function AccessLogSection({ log }) {
  return (
    <section className="mt-8">
      <div className="flex items-center gap-2 mb-3">
        <Eye size={18} className="text-[#1B3A5C]" />
        <h2 className="text-lg font-semibold text-[#1F2933]">Qui a consulté mes documents</h2>
      </div>
      <p className="text-xs text-[#3E4C59] mb-3">
        Journal des consultations par les donneurs d'ordre vérifiés. Conservé 3 ans.
      </p>
      {log.length === 0 ? (
        <div className="card p-4 text-sm text-[#3E4C59]" data-testid="access-log-empty">
          Aucune consultation pour le moment.
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="table-compact w-full" data-testid="access-log-table">
            <thead>
              <tr><th>Date</th><th>Consultant</th><th>SIRET</th><th>Document</th></tr>
            </thead>
            <tbody>
              {log.map((r, i) => (
                <tr key={i} data-testid={`access-log-row-${i}`}>
                  <td className="whitespace-nowrap">{new Date(r.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</td>
                  <td>{r.consultant_raison_sociale || "—"}</td>
                  <td className="text-xs text-[#3E4C59]">{r.consultant_siret || "—"}</td>
                  <td>{L.docTypes[r.doc_type] || r.doc_type}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
